import React, { FC, useEffect, useState } from 'react';
import {
  AddPaymentMethodFormikType,
  SubmitNewPaymentMethodType,
} from './AddPaymentMethodForm';
import { useFormikContext } from 'formik';
import {
  AdyenCheckout,
  translations,
  useAdyenScripts,
} from 'Shared/hooks/useAdyenScripts';
import { env } from 'Shared/constants/env';

type AdyenAddPaymentResponse = {
  data: {
    paymentMethod:
      | {
          type: 'scheme';
          encryptedCardNumber: string;
          encryptedExpiryMonth: string;
          encryptedExpiryYear: string;
          encryptedSecurityCode: string;
        }
      | {
          type: 'ach';
          encryptedBankAccountNumber: string;
          encryptedBankLocationId: string;
          ownerName: string;
        };
  };
  isValid: boolean;
};

type AdyenAddPaymentFormPropsType = {
  paymentType: string;
  config: {
    locale: string;
    paymentMethodsResponse: unknown;
    amount: {
      currency: string;
    };
    channel: string;
    showPayButton: boolean;
  };
  name: string;
  submitForm: SubmitNewPaymentMethodType;
};
const adyenCardClass = '.adyen-checkout__payment-method--card';
const adyenAchClass = '.adyen-checkout__payment-method--ach';

export const AdyenAddPaymentForm: FC<AdyenAddPaymentFormPropsType> = ({
  paymentType,
  config,
  submitForm,
  name,
}) => {
  const [checkout, setCheckout] = useState<AdyenCheckout>();
  const { setStatus, setSubmitting, setFieldValue, values } =
    useFormikContext<AddPaymentMethodFormikType>();
  const { environment, scriptLoaded } = useAdyenScripts();

  useEffect(() => {
    if (paymentType === 'ENCRYPTED_CREDIT_CARD') {
      (document.querySelector(adyenCardClass) as HTMLElement)?.click();
    } else {
      (document.querySelector(adyenAchClass) as HTMLElement)?.click();
    }
  }, [paymentType, values.adyen]);

  useEffect(() => {
    async function initializeAdyenCheckout() {
      if (scriptLoaded) {
        const adyenConfiguration = {
          ...config,
          setStatusAutomatically: false,
          environment: environment,
          translations: translations,
          clientKey: env.adyenClientKey,
          onSubmit: (encryptedData: AdyenAddPaymentResponse) => {
            if (encryptedData.isValid) {
              const addPaymentMethodPayload =
                encryptedData.data.paymentMethod.type === 'ach'
                  ? {
                      type: 'ENCRYPTED_ACH',
                      accountOwner: encryptedData.data.paymentMethod.ownerName,
                      accountNumber:
                        encryptedData.data.paymentMethod
                          .encryptedBankAccountNumber,
                      routingNumber:
                        encryptedData.data.paymentMethod
                          .encryptedBankLocationId,
                    }
                  : {
                      type: 'ENCRYPTED_CREDIT_CARD',
                      cardHolder: name,
                      number:
                        encryptedData.data.paymentMethod.encryptedCardNumber,
                      cvc: encryptedData.data.paymentMethod
                        .encryptedSecurityCode,
                      expirationMonth:
                        encryptedData.data.paymentMethod.encryptedExpiryMonth,
                      expirationYear:
                        encryptedData.data.paymentMethod.encryptedExpiryYear,
                    };
              submitForm(addPaymentMethodPayload)
                .catch((err) => {
                  setStatus(err.response.data.message ?? err.message);
                })
                .finally(() => {
                  setSubmitting(false);
                });
            } else {
              setSubmitting(false);
            }
          },
        };

        setCheckout(await window.AdyenCheckout(adyenConfiguration));
      }
    }

    initializeAdyenCheckout();
  }, [
    config,
    submitForm,
    setStatus,
    setSubmitting,
    setFieldValue,
    scriptLoaded,
    name,
    environment,
  ]);

  React.useEffect(() => {
    if (!checkout) return;

    const adyen = checkout.create('dropin').mount('#credit-card-form');
    setFieldValue('adyen', adyen);
  }, [checkout, setFieldValue]);
  return <div id="credit-card-form" />;
};
